import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddUser,
  ApiStatusResponse,
  UserApiResponse,
  UserResponse,
} from "../types/taskTypes";
import {
  createUser,
  deleteUser,
  fetchUserList,
  fetchUserProfile,
  getUserDetail,
  updateUser,
} from "../services/authServices";
interface FetchUserThunkParams {
  page?: number;
  pagesize?: number;
  search?: string;
  order_by?: string;
  order_dir?: string;
}
// ✅ State interface
interface UserState {
  users: UserResponse[];
  loading: boolean;
  error: string | null;
  message: string | null;
  status: boolean | null;
  status_code: number | null;
  user?: UserResponse | null; // ✅ personal profile info
  total: number;
}

// ✅ Initial state
const initialState: UserState = {
  users: [],
  user: null,
  loading: false,
  error: null,
  message: null,
  status: null,
  status_code: null,
  total: 0,
};

// ✅ Thunk: Add New User (accepts FormData)
export const addNewUser = createAsyncThunk(
  "user/add",
  async (form: FormData, { rejectWithValue }) => {
    try {
      return await createUser(form);
    } catch (error: any) {
      return rejectWithValue(error || "User creation failed");
    }
  }
);

export const fetchAllUsers = createAsyncThunk<
  UserApiResponse,
  FetchUserThunkParams,
  { rejectValue: string }
>("user/fetchAll", async (params, { rejectWithValue }) => {
  try {
    return await fetchUserList(params);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to fetch users");
  }
});
// Update User
export const updateUserById = createAsyncThunk<
  ApiStatusResponse,
  { id: number; formData: FormData },
  { rejectValue: string }
>("user/update", async ({ id, formData }, { rejectWithValue }) => {
  try {
    const res = await updateUser(id, formData); // ⬅️ From your service
    return res;
  } catch (err: any) {
    return rejectWithValue(err || "Failed to update user");
  }
});

// ✅ Thunk: Delete User
export const deleteUserById = createAsyncThunk<
  ApiStatusResponse,
  number,
  { rejectValue: string }
>("user/delete", async (id, { rejectWithValue }) => {
  try {
    return await deleteUser(id);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to delete user");
  }
});

// get specific User
export const getUserById = createAsyncThunk<
  ApiStatusResponse,
  number,
  { rejectValue: string }
>("users/detail", async (id, { rejectWithValue }) => {
  try {
    return await getUserDetail(id);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to delete user");
  }
});

// get user detail
export const fetchUserMe = createAsyncThunk<
  UserResponse, // response type
  void, // no argument
  { rejectValue: string }
>("auth/fetchUserMe", async (_, { rejectWithValue }) => {
  try {
    return await fetchUserProfile(); // use service
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to fetch user profile");
  }
});
// ✅ Slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Add User
    builder.addCase(addNewUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      addNewUser.fulfilled,
      (state, action: PayloadAction<UserResponse>) => {
        state.users.push(action.payload);
        state.loading = false;
      }
    );
    builder.addCase(addNewUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch All Users
    builder.addCase(fetchAllUsers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchAllUsers.fulfilled,
      (state, action: PayloadAction<UserApiResponse>) => {
        state.users = action.payload.data.data; // ✅ Fix here
        state.message = action.payload.message;
        state.total = action.payload.data.total;
        state.status = action.payload.status;
        state.status_code = action.payload.status_code;
        state.loading = false;
      }
    );
    builder.addCase(fetchAllUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete User
    builder.addCase(deleteUserById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteUserById.fulfilled, (state, action) => {
      const deletedUserId = action.meta.arg; // The user ID passed to the thunk
      state.users = state.users.filter((user) => user.id !== deletedUserId);
      state.total = state.total > 0 ? state.total - 1 : 0; // ✅ Decrement total
      state.loading = false;
      state.status = action.payload.status;
      state.status_code = action.payload.status_code;
      state.message = action.payload.message;
    });
    builder.addCase(deleteUserById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to delete user";
    });

    // get spefic user detail
    builder.addCase(getUserById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getUserById.fulfilled, (state, action) => {
      state.user = action.payload.data;
      state.loading = false;
    });
    builder.addCase(getUserById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to user detail";
    });

    // Update User
    builder.addCase(updateUserById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserById.fulfilled, (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      state.status_code = action.payload.status_code;
      state.message = action.payload.message;

      const updatedUser = action.payload.data;

      if (updatedUser) {
        state.users = state.users.map((user) =>
          user.id === updatedUser.id ? { ...user, ...updatedUser } : user
        );
      }
    });

    builder.addCase(updateUserById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to update user";
    });

    //get user detail

    builder.addCase(fetchUserMe.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchUserMe.fulfilled, (state, action) => {
      state.user = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchUserMe.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to load user";
    });

    // get specific user detail
  },
});

export default userSlice.reducer;
